import { ApiBase } from '@/api/methods/ApiBase'
import { ApiResponseTemporaryStudent, ApiResponseTemporaryStudents } from '@/api/responses/manage/temporaryStudents'

export namespace TemporaryStudentsApi {
  /**
   * オプション引数(非必須のパラメータ)の型
   */
  type optionParams = {
    /** 学年コード */
    gradeCode?: string
    /** 教室ID */
    branchId?: number
    /** コースID */
    courseId?: number
    /** クラスID */
    classId?: number
    /** 検索文字列 */
    searchText?: string
  }

  /**
   * 仮生徒一覧取得API
   *
   * @param pageNumber ページ番号
   * @param count 表示件数
   * @param options 非必須の絞り込み条件(gradeCode, branchId, courseId, classId, searchText)
   * @returns 生徒一覧
   */
  export async function getTemporaryStudents(
    pageNumber: number,
    count: number,
    options: optionParams
  ): Promise<ApiResponseTemporaryStudents> {
    const params = {
      pageNumber: pageNumber,
      count: count,
    }
    // 学年コードが存在すれば追加
    if (options.gradeCode) {
      Object.assign(params, { gradeCode: options.gradeCode })
    }
    // 教室IDが存在すれば追加
    if (options.branchId) {
      Object.assign(params, { branchId: options.branchId })
    }
    // コースIDが追加すれば追加
    if (options.courseId) {
      Object.assign(params, { courseId: options.courseId })
    }
    // クラスIDが存在すれば追加
    if (options.classId) {
      Object.assign(params, { classId: options.classId })
    }
    // 検索文字列があれば追加
    if (options.searchText) {
      Object.assign(params, { searchText: options.searchText })
    }
    return await ApiBase.get('manage/temporary/students', params)
  }

  /**
   * 仮生徒単体取得API
   *
   * @param userId ユーザID
   * @returns 生徒情報
   */
  export async function getTemporaryStudent(userId: number): Promise<ApiResponseTemporaryStudent> {
    return await ApiBase.get(`manage/temporary/students/${userId}`)
  }

  /**
   * 仮生徒登録API
   *
   * @param nickname ニックネーム
   * @param loginId ログインID
   * @param password パスワード
   * @param gradeCode 学年コード
   * @param branchId 教室
   * @param academyClassId クラスID
   * @param options 非必須のパラメータ
   * @param options.identifyTag 生徒識別子(任意)
   */
  export async function createTemporaryStudent(
    nickname: string,
    loginId: string,
    password: string,
    gradeCode: string,
    branchId: number,
    academyClassId: number,
    options?: {
      /** 生徒識別子 */
      identifyTag?: string
    }
  ): Promise<void> {
    const params = {
      nickname: nickname,
      loginId: loginId,
      password: password,
      gradeCode: gradeCode,
      branchId: branchId,
      classId: academyClassId,
    }

    // 識別子が指定されていれば追加
    if (options?.identifyTag) {
      Object.assign(params, { identifyTag: options.identifyTag })
    }
    await ApiBase.post('manage/temporary/students', params)
  }

  /**
   * 仮生徒情報更新API
   *
   * @param userId ユーザID
   * @param nickname ニックネーム
   * @param identifyTag 生徒識別子
   * @param gradeCode 学年コード
   * @param branchId 教室
   * @param academyCourseId コースID
   * @param academyClassId クラスID
   */
  export async function updateTemporaryStudent(
    userId: number,
    nickname: string,
    identifyTag: string,
    gradeCode: string,
    branchId: number,
    academyCourseId: number,
    academyClassId: number
  ): Promise<void> {
    const params = {
      nickname: nickname,
      identifyTag: identifyTag,
      gradeCode: gradeCode,
      branchId: branchId,
      courseId: academyCourseId,
      classId: academyClassId,
    }
    await ApiBase.post(`manage/temporary/students/${userId}/update`, params)
  }
}
